/**
 * Stills are kept as JPEG (Dax, 2026-10-05). The browser makes that JPEG for every photo it can decode:
 * upright by its EXIF orientation, shrunk (never enlarged) to STILL_LONG_EDGE, at STILL_JPEG_QUALITY, under
 * the photo's own name so a photo dropped again is recognised. Safari decodes HEIC; Chrome, Edge and Firefox
 * do not, and then the original is sent for the worker to convert. Built-in APIs only.
 */
import { STILL_JPEG_QUALITY, STILL_LONG_EDGE } from '@studio/contracts';

const JPEG = 'image/jpeg';

/** The photo as the JPEG we keep, or null when this browser cannot decode it. */
export async function toStillJpeg(file: File): Promise<File | null> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return null;
  }
  try {
    const scale = Math.min(1, STILL_LONG_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, JPEG, STILL_JPEG_QUALITY));
    return blob ? new File([blob], file.name, { type: JPEG, lastModified: file.lastModified }) : null;
  } finally {
    bitmap.close();
  }
}
